import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import { ArrowsClockwise, ArrowSquareOut, X } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import { assignTeamCategory, createTeam, updateTeam, type Team } from '@/api/teams';
import { listTeamCategories } from '@/api/teamCategories';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { resolve } from '@/lib/teamColor';
import { cn } from '@/lib/utils';

// TeamModal handles both "New team" and "Edit team" via the optional `team`
// prop:
//   - undefined → create mode (empty form, POST /teams)
//   - Team       → edit mode (prefilled form, PATCH /teams/{id})
//
// Color-picker behavior: the live `name` value drives the auto-derived
// preview in real time. The user can either accept the auto color (leaves
// the override blank) or pick a custom one via the native color input,
// which switches `colorOverride` on. A "Reset to auto" button clears the
// override and snaps back to the deterministic color.
//
// Server is the authority on the color format (CHECK constraint), but we
// also enforce the same hex pattern client-side so the "save" button only
// enables when the value would land cleanly.
export interface TeamModalProps {
  open: boolean;
  onClose: () => void;
  slug: string;
  // Pass an existing team to edit; undefined = create.
  team?: Team;
}

export function TeamModal({ open, onClose, slug, team }: TeamModalProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const manageCategoriesHref = `/orgs/${encodeURIComponent(routeSlug ?? slug)}/settings/team-categories`;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [colorOverride, setColorOverride] = useState(''); // '' = use auto
  // public_visibility — toggle on the edit modal. Default FALSE for new
  // teams (privacy first; admins opt in). Edit mode prefills from team.
  const [publicVisibility, setPublicVisibility] = useState(false);
  // categoryID — '' = uncategorised. Edit mode prefills from team.
  const [categoryID, setCategoryID] = useState('');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Load categories for the selector. Cheap query (<50 per org); same
  // ['team-categories', slug] cache key the settings page uses.
  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token && !!slug && open,
  });
  const categories = useMemo(
    () => categoriesQuery.data?.categories ?? [],
    [categoriesQuery.data],
  );

  // Reset form on every open. Edit mode prefills from `team`; create mode
  // starts blank. Auto-focus the name input — most flows here are quick
  // renames or fast new-team entries.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setName(team?.name ?? '');
      setDescription(team?.description ?? '');
      setColorOverride(team?.color ?? '');
      setPublicVisibility(team?.public_visibility ?? false);
      setCategoryID(team?.category_id ?? '');
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, team]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  // Derived palette previewed in the swatch. When the user has set an
  // override, the swatch reflects that hex; otherwise it tracks the live
  // name input via the deterministic hash.
  const palette = useMemo(
    () => resolve(name || team?.name || '', colorOverride || null),
    [name, team?.name, colorOverride],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        color: colorOverride.trim().toLowerCase(),
        public_visibility: publicVisibility,
      };
      if (team) {
        // Update + optional category assignment. The dedicated PUT keeps
        // the main PATCH payload simple, at the cost of a second round-
        // trip when the category actually changed. Skip the extra call
        // when the value is unchanged.
        const updated = await updateTeam(token, slug, team.id, payload);
        if (categoryID !== (team.category_id ?? '')) {
          return assignTeamCategory(token, slug, team.id, categoryID);
        }
        return updated;
      }
      return createTeam(token, slug, {
        ...payload,
        category_id: categoryID || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
      // The members list shows team membership too once that's wired —
      // invalidating now means the team-color chip on member rows will
      // pick up renames/recolor without a manual refresh.
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
      // team_count on each category shifts on assign — refresh the
      // settings page if it's open in another tab.
      void queryClient.invalidateQueries({ queryKey: ['team-categories', slug] });
      onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
    },
  });

  const isHexValid = colorOverride === '' || /^#[0-9a-f]{6}$/i.test(colorOverride.trim());
  const canSubmit = name.trim().length > 0 && isHexValid && !mutation.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setErrorCode(null);
    mutation.mutate();
  };

  const titleKey = team ? 'teams.modal.editTitle' : 'teams.modal.createTitle';
  const submitKey = team ? 'teams.modal.save' : 'teams.modal.create';
  const submittingKey = team ? 'teams.modal.saving' : 'teams.modal.creating';

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
      onClick={(e) => { if (e.target === dialogRef.current) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex w-[min(34rem,90vw)] flex-col gap-4 p-6"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill"
              style={{ background: palette.bg, border: `2px solid ${palette.border}` }}
            >
              <span
                className="h-3 w-3 rounded-pill"
                style={{ background: palette.dot }}
              />
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {t(titleKey)}
            </h2>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('teams.modal.name')}
          <Input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrorCode(null);
            }}
            required
            maxLength={120}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            placeholder={t('teams.modal.namePlaceholder')}
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('teams.modal.description')}
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={3}
            className={cn(
              'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground',
              'ring-offset-background placeholder:text-muted-foreground',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            )}
            placeholder={t('teams.modal.descriptionPlaceholder')}
          />
        </label>

        <div className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          <label htmlFor="team-modal-category">{t('teams.modal.category')}</label>
          {/* Sentinel value "__none__" because Radix Select disallows empty
              strings as item values; we map it back to '' on the way out. */}
          <Select
            value={categoryID === '' ? '__none__' : categoryID}
            onValueChange={(v) => setCategoryID(v === '__none__' ? '' : v)}
          >
            <SelectTrigger id="team-modal-category">
              <SelectValue />
            </SelectTrigger>
            {/* Portal into the dialog so the dropdown sits in the same
                top-layer as the modal — Radix's default body-level portal
                renders behind a native <dialog>. */}
            <SelectContent container={dialogRef.current}>
              <SelectItem value="__none__">{t('teams.modal.categoryNone')}</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-normal text-muted-foreground">
              {t('teams.modal.categoryHelp')}
            </span>
            <a
              href={manageCategoriesHref}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex shrink-0 items-center gap-1 rounded-pill px-2 py-1 text-xs font-medium text-foreground/80 underline-offset-2 hover:bg-muted hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('teams.modal.categoryManage')}
              <ArrowSquareOut className="h-3 w-3" aria-hidden weight="bold" />
            </a>
          </div>
        </div>

        <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
          <legend className="px-1 text-sm font-medium text-foreground">
            {t('teams.modal.color')}
          </legend>
          <p className="text-xs text-muted-foreground">
            {colorOverride
              ? t('teams.modal.colorOverridden')
              : t('teams.modal.colorAuto')}
          </p>
          <div className="flex items-center gap-2">
            <input
              type="color"
              // The native color input requires a 7-char hex; when no
              // override is set, seed it with the live auto color so
              // clicking the swatch opens the picker on the right hue.
              value={colorOverride || palette.hex}
              onChange={(e) => {
                setColorOverride(e.target.value.toLowerCase());
                setErrorCode(null);
              }}
              aria-label={t('teams.modal.colorPicker')}
              className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
            />
            <Input
              type="text"
              value={colorOverride}
              onChange={(e) => {
                setColorOverride(e.target.value.toLowerCase());
                setErrorCode(null);
              }}
              placeholder={palette.hex}
              maxLength={7}
              spellCheck={false}
              className="flex-1 font-mono"
              aria-invalid={!isHexValid || undefined}
            />
            {colorOverride && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setColorOverride('')}
                title={t('teams.modal.colorReset')}
              >
                <ArrowsClockwise className="h-4 w-4" />
                {t('teams.modal.colorReset')}
              </Button>
            )}
          </div>
          {!isHexValid && (
            <p className="text-xs text-destructive">{t('teams.modal.colorInvalid')}</p>
          )}
        </fieldset>

        {/* Public visibility toggle — controls whether the team shows
            on /teams, in team_rail blocks, and at its own /team/<slug>
            URL. Default off for new teams (admin opts in). */}
        <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              checked={publicVisibility}
              onChange={(e) => setPublicVisibility(e.target.checked)}
              className="mt-0.5"
            />
            <span className="flex flex-col gap-1">
              <span className="font-medium text-foreground">
                {t('teams.modal.publicVisibility.label', 'Show on public website')}
              </span>
              <span className="text-xs text-muted-foreground">
                {t(
                  'teams.modal.publicVisibility.help',
                  'When on, this team appears on the /teams page and anywhere the website lists teams. Turn off for internal teams (staff, reserves) you don’t want listed publicly.',
                )}
              </span>
            </span>
          </label>
        </fieldset>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`teams.modal.error.${errorCode}`, {
              defaultValue: t('teams.modal.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {mutation.isPending ? t(submittingKey) : t(submitKey)}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}
